import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface GridItem {
  id: string;
  section: 'section-key-changes' | 'section-whats-driving-it' | 'section-take-action';
}

interface Block {
  type: string;
  label: string;
  source: string;
  agent?: string;
  agents?: string[];
  icon?: string;
  threshold?: number;
  trigger?: string;
  options?: string[];
  groupBy?: string;
}

interface Section {
  id: string;
  label: string;
  description: string;
  blocks: Block[];
}

interface DashboardLayout {
  sections: Section[];
}

interface DragDropContextType {
  maximizedChartId: string | null;
  setMaximizedChartId: (id: string | null) => void;
  draggedItemId: string | null;
  setDraggedItemId: (id: string | null) => void;
  
  // Section-specific grid controls
  keyChangesGridCols: number;
  whatsDrivingItGridCols: number;
  takeActionGridCols: number;
  setKeyChangesGridCols: (cols: number) => void;
  setWhatsDrivingItGridCols: (cols: number) => void;
  setTakeActionGridCols: (cols: number) => void;
  
  // Section-specific chart orders
  keyChangesChartOrder: string[];
  whatsDrivingItChartOrder: string[];
  takeActionChartOrder: string[];
  setKeyChangesChartOrder: (order: string[]) => void;
  setWhatsDrivingItChartOrder: (order: string[]) => void;
  setTakeActionChartOrder: (order: string[]) => void;
  
  // Dashboard layout
  dashboardLayout: DashboardLayout;
  
  gridItems: GridItem[];
}

const DragDropContext = createContext<DragDropContextType | undefined>(undefined);

// Default layout configuration from JSON with professional icons
const defaultLayout: DashboardLayout = {
  sections: [
    {
      id: "section-key-changes",
      label: "Key Changes",
      description: "Latest shifts in cash, credit, and costs.",
      blocks: [
        {
          type: "KPICard",
          label: "Cash Available",
          source: "ERP / Bank Feed",
          agent: "Poniente - Cash Flow Stabilizer",
          icon: "DollarSign"
        },
        {
          type: "KPICard",
          label: "Credit Utilization",
          source: "Bank Feed / Allianz",
          agent: "Levante - Credit Risk Manager",
          icon: "CreditCard",
          threshold: 85
        },
        {
          type: "KPICard",
          label: "Overdue Receivables",
          source: "ERP",
          agent: "Tell Tale - Email Parser",
          icon: "Clock"
        },
        {
          type: "LineChart",
          label: "Cash Flow Forecast (30/60/90 Days)",
          source: "Cash Flow Agent",
          agent: "Poniente - Cash Flow Stabilizer"
        }
      ]
    },
    {
      id: "section-whats-driving-it",
      label: "What's Driving It",
      description: "Understand the 'why' behind key changes.",
      blocks: [
        {
          type: "VarianceTable",
          label: "Budget vs Actual (by Driver)",
          source: "ERP",
          agent: "Full Send - Quote to Cash Orchestrator"
        },
        {
          type: "LineChart",
          label: "Revenue Forecast (QTC + FX Impact)",
          source: "QTC Agent",
          agent: "Full Send - Quote to Cash Orchestrator"
        }
      ]
    },
    {
      id: "section-take-action",
      label: "Take Action",
      description: "Simulate scenarios and take recommended steps.",
      blocks: [
        {
          type: "MultiInputChartBlock",
          label: "Run What-If Simulation",
          source: "What-If Engine",
          agents: [
            "Tell Tale - Email Parser",
            "Full Send - Quote to Cash Orchestrator",
            "Poniente - Cash Flow Stabilizer",
            "Levante - Credit Risk Manager"
          ]
        },
        {
          type: "ActionListCard",
          label: "Windrose-Recommended Actions",
          source: "All Agents",
          agents: [
            "Tell Tale - Email Parser",
            "Full Send - Quote to Cash Orchestrator",
            "Poniente - Cash Flow Stabilizer",
            "Levante - Credit Risk Manager"
          ]
        }
      ]
    }
  ]
};

export function DragDropProvider({ children }: { children: ReactNode }) {
  const [maximizedChartId, setMaximizedChartId] = useState<string | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  
  // Section-specific grid columns - DEMO OPTIMIZED
  const [keyChangesGridCols, setKeyChangesGridCols] = useState(2); // Changed from 3 to 2 for larger charts
  const [whatsDrivingItGridCols, setWhatsDrivingItGridCols] = useState(2);
  const [takeActionGridCols, setTakeActionGridCols] = useState(2);
  
  // Updated chart orders to include new components - DEMO OPTIMIZED
  const [keyChangesChartOrder, setKeyChangesChartOrder] = useState<string[]>([
    'cash-position',        // LARGE: Cash Position Chart (most impressive)
    'waterfall-cash-flow',  // LARGE: Waterfall Cash Flow Chart (visual impact)
    'kpi-bar',              // MEDIUM: KPI Blocks (Cash, Credit, AR)
    'available-credit',     // MEDIUM: Credit Utilization details
  ]);
  
  // SECTION 2: "Why It Happened" - Variance analysis and insights
  const [whatsDrivingItChartOrder, setWhatsDrivingItChartOrder] = useState<string[]>([
    'budget-actuals',       // LARGE: Budget vs Actual (by Driver)
    'scenario-planning',    // LARGE: Revenue Forecast with delays/FX
  ]);
  
  // SECTION 3: "What Can I Do?" - Simulation and action tools
  const [takeActionChartOrder, setTakeActionChartOrder] = useState<string[]>([
    'what-if-simulation',  // LARGE: What-If Simulation (most interactive)
    'ai-recommendations',   // MEDIUM: Windrose-Recommended Actions
    'bills-summary',       // MEDIUM: Bills Overview
  ]);

  const gridItems: GridItem[] = [
    ...keyChangesChartOrder.map(id => ({ id, section: 'section-key-changes' as const })),
    ...whatsDrivingItChartOrder.map(id => ({ id, section: 'section-whats-driving-it' as const })),
    ...takeActionChartOrder.map(id => ({ id, section: 'section-take-action' as const }))
  ];

  return (
    <DragDropContext.Provider value={{
      maximizedChartId,
      setMaximizedChartId,
      draggedItemId,
      setDraggedItemId,
      keyChangesGridCols,
      whatsDrivingItGridCols,
      takeActionGridCols,
      setKeyChangesGridCols,
      setWhatsDrivingItGridCols,
      setTakeActionGridCols,
      keyChangesChartOrder,
      whatsDrivingItChartOrder,
      takeActionChartOrder,
      setKeyChangesChartOrder,
      setWhatsDrivingItChartOrder,
      setTakeActionChartOrder,
      dashboardLayout: defaultLayout,
      gridItems
    }}>
      {children}
    </DragDropContext.Provider>
  );
}

export function useDragDrop() {
  const context = useContext(DragDropContext);
  if (context === undefined) {
    throw new Error('useDragDrop must be used within a DragDropProvider');
  }
  return context;
}
